import { create } from 'zustand';
import authService from '@/services/authService';
import type {
  User,
  LoginCredentials,
  RegisterData,
  ChangePasswordData,
} from '@/types/auth';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: (allDevices?: boolean) => Promise<void>;
  loadUser: () => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  clearError: () => void;
  setError: (error: string) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: authService.getUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  /**
   * Login user with email and password
   */
  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(credentials);

      if (response.success) {
        const { user, tokens } = response.data;
        authService.setTokens(tokens.accessToken, tokens.refreshToken);
        authService.setUser(user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Login failed. Please try again.';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  /**
   * Register a new user
   */
  register: async (data: RegisterData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(data);

      if (response.success) {
        const { user, tokens } = response.data;
        authService.setTokens(tokens.accessToken, tokens.refreshToken);
        authService.setUser(user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Registration failed. Please try again.';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  /**
   * Logout user
   * @param allDevices - If true, logout from all devices
   */
  logout: async (allDevices = false) => {
    set({ isLoading: true, error: null });
    try {
      if (allDevices) {
        await authService.logoutAll();
      } else {
        await authService.logout();
      }
      authService.clearAuth();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      // Even if logout API call fails, clear local data
      authService.clearAuth();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  /**
   * Load current user from API (refresh user data)
   */
  loadUser: async () => {
    // Only load if we have a token
    if (!authService.isAuthenticated()) {
      set({ user: null, isAuthenticated: false });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await authService.getCurrentUser();
      if (response.success) {
        const { user } = response.data;
        authService.setUser(user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error('Failed to load user');
      }
    } catch (error: any) {
      // If loading user fails, clear auth
      authService.clearAuth();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  /**
   * Change user password
   */
  changePassword: async (data: ChangePasswordData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.changePassword(data);
      if (response.success) {
        set({ isLoading: false, error: null });
      } else {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to change password. Please try again.';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  /**
   * Clear error message
   */
  clearError: () => set({ error: null }),

  /**
   * Set error message
   */
  setError: (error: string) => set({ error }),
}));
